// ============================================================
// TMR Portal Admin — leituras e escritas do rating de crédito, sobre @tmr/api-client (26)
// ============================================================
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { api } from '@tmr/api-client';
import { creditKeys } from '../queryClient';
import type {
  CreditProfile,
  DecideManualReviewRequest,
  ManualReviewDecisionView,
  ManualReviewDetail,
  ManualReviewList,
  RequestBureauReportRequest,
  RequestBureauReportResponse,
} from './contract';

/**
 * The sales waiting for the TMR, oldest first.
 *
 * @param enabled Whether the operator holds `credit.reviews.read` — false skips a request that would 403.
 */
export function useManualReviews(enabled = true): UseQueryResult<ManualReviewList> {
  return useQuery({
    queryKey: creditKeys.reviews(),
    queryFn: ({ signal }) => api.get<ManualReviewList>('/credit/manual-reviews', { signal }),
    enabled,
  });
}

/**
 * One evaluation with what decided it.
 *
 * @param evaluationId The evaluation, or undefined to skip the request.
 */
export function useManualReview(evaluationId: string | undefined): UseQueryResult<ManualReviewDetail> {
  return useQuery({
    queryKey: creditKeys.review(evaluationId ?? ''),
    queryFn: ({ signal }) => api.get<ManualReviewDetail>(`/credit/manual-reviews/${evaluationId}`, { signal }),
    enabled: Boolean(evaluationId),
  });
}

/**
 * A customer's credit at the TMR. The CPF goes in the body of a POST — never in the URL, which the backend's
 * instrumentation records (`26` §13) — so the lookup is a mutation the screen triggers, not a cached query.
 */
export function useCreditProfileSearch() {
  return useMutation({
    mutationFn: (document: string) => api.post<CreditProfile>('/credit/profiles/search', { document }),
  });
}

/**
 * Consults the bureau for a customer — a paid query, unless `force` is false and a report of the last 3 months answers.
 * Never retried by the screen: a failure is shown with the server's reason, and a new attempt is a new confirmation
 * with a new key.
 */
export function useRequestBureauReport() {
  return useMutation({
    mutationFn: (request: RequestBureauReportRequest) =>
      api.post<RequestBureauReportResponse>('/credit/bureau-reports', request),
  });
}

/**
 * Approves or rejects a sale. The v1.1 applies the decision within a minute; the queue and the detail are
 * invalidated so the screens read it back from the server.
 *
 * @param evaluationId The evaluation decided.
 */
export function useDecideManualReview(evaluationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: DecideManualReviewRequest) =>
      api.post<ManualReviewDecisionView>(`/credit/manual-reviews/${evaluationId}/decision`, request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: creditKeys.all });
    },
  });
}
